import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send, CalendarCheck2, MessageCircle } from "lucide-react";
import { CLINIC, SERVICES } from "../data/clinic";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = {
  name: "",
  email: "",
  phone: "",
  service: "General Enquiry",
  area: "",
  address: "",
  message: "",
};

export default function ContactSection() {
  const [form, setForm] = useState(initialForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in all required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiries`, form);
      setDone(true);
      setForm(initialForm);
      toast.success("Thank you — your enquiry has been received.");
    } catch (err) {
      console.error(err);
      toast.error("Sorry, something went wrong. Please call us instead.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="lfc-section relative overflow-hidden">
      <div className="absolute inset-0 bg-gradient-to-b from-[#251047]/30 via-[#1B0B3A] to-[#1B0B3A] pointer-events-none" />
      <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-[#2BB8B4]/12 blur-[140px] pointer-events-none" />
      <div className="absolute bottom-0 left-1/4 w-[420px] h-[420px] rounded-full bg-[#7B46D9]/20 blur-[120px] pointer-events-none" />

      <div className="lfc-container relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Left: copy & details */}
          <div className="lg:col-span-5">
            <div className="lfc-eyebrow mb-4">Book an appointment</div>
            <h2 className="lfc-h2">
              Take the first step towards healthier feet.
            </h2>
            <p className="mt-6 text-lg text-[#C2B4E6] leading-relaxed">
              Whether you require routine care, diabetic foot assessment, or help with a specific concern —
              we'd love to hear from you. Fill out the form and we'll get back within one working day.
            </p>

            <div className="mt-10 space-y-5">
              <DetailRow
                icon={Phone}
                label="Call or text"
                value={CLINIC.phone}
                href={`tel:${CLINIC.phoneRaw}`}
                testId="contact-phone"
              />
              <DetailRow
                icon={MessageCircle}
                label="WhatsApp"
                value="Message us on WhatsApp"
                href={CLINIC.whatsapp}
                external
                testId="contact-whatsapp"
              />
              <DetailRow
                icon={Mail}
                label="Email"
                value={CLINIC.email}
                href={`mailto:${CLINIC.email}`}
                testId="contact-email"
              />
              <DetailRow
                icon={MapPin}
                label="Service area"
                value={CLINIC.areas.join(" · ")}
                testId="contact-areas"
              />
            </div>

            <div className="mt-10 lfc-card p-6 flex items-start gap-4">
              <div className="w-11 h-11 rounded-full grid place-items-center bg-[#2BB8B4] text-[#0E0524] shrink-0">
                <CalendarCheck2 className="w-5 h-5" strokeWidth={1.8} />
              </div>
              <div>
                <div className="text-[15px] font-semibold text-white" style={{ fontFamily: "Outfit" }}>
                  Flexible home visits
                </div>
                <p className="text-[14.5px] text-[#C2B4E6] mt-1 leading-relaxed">
                  Daytime, evening and weekend slots available where possible.
                  Let us know your preferred time and we'll do our best to accommodate.
                </p>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="lg:col-span-7">
            <div className="lfc-card p-7 lg:p-10">
              {done ? (
                <SuccessMessage onAnother={() => setDone(false)} />
              ) : (
                <form onSubmit={submit} data-testid="enquiry-form" className="space-y-5">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Full name *">
                      <input
                        data-testid="form-name"
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={onChange}
                        required
                        className="lfc-input"
                        placeholder="Jane Smith"
                      />
                    </Field>
                    <Field label="Phone number *">
                      <input
                        data-testid="form-phone"
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={onChange}
                        required
                        className="lfc-input"
                        placeholder="07xxx xxx xxx"
                      />
                    </Field>
                  </div>

                  <Field label="Email *">
                    <input
                      data-testid="form-email"
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={onChange}
                      required
                      className="lfc-input"
                      placeholder="you@email.com"
                    />
                  </Field>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <Field label="Service of interest">
                      <select
                        data-testid="form-service"
                        name="service"
                        value={form.service}
                        onChange={onChange}
                        className="lfc-input"
                      >
                        <option>General Enquiry</option>
                        {SERVICES.map((s) => (
                          <option key={s.id} value={s.title}>
                            {s.title}
                          </option>
                        ))}
                      </select>
                    </Field>
                    <Field label="Your area">
                      <select
                        data-testid="form-area"
                        name="area"
                        value={form.area}
                        onChange={onChange}
                        className="lfc-input"
                      >
                        <option value="">Select your area…</option>
                        {CLINIC.areas.map((a) => (
                          <option key={a} value={a}>
                            {a}
                          </option>
                        ))}
                        <option value="Other">Other / not listed</option>
                      </select>
                    </Field>
                  </div>

                  <Field label="Your address (for the home visit)">
                    <textarea
                      data-testid="form-address"
                      name="address"
                      value={form.address}
                      onChange={onChange}
                      rows={2}
                      className="lfc-input resize-none"
                      placeholder="Street, town/city, postcode"
                    />
                  </Field>

                  <Field label="Tell us a little about what you need *">
                    <textarea
                      data-testid="form-message"
                      name="message"
                      value={form.message}
                      onChange={onChange}
                      required
                      rows={5}
                      className="lfc-input resize-none"
                      placeholder="E.g. Routine nail care for an elderly relative; mobility limited."
                    />
                  </Field>

                  <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                    <p className="text-[13px] text-[#8B7FB5] max-w-md">
                      By submitting, you agree to be contacted regarding your enquiry.
                      We never share your details.
                    </p>
                    <button
                      data-testid="form-submit"
                      type="submit"
                      disabled={submitting}
                      className="lfc-btn-primary disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                      {submitting ? "Sending…" : "Send enquiry"}
                      {!submitting && <Send className="w-4 h-4" strokeWidth={2} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="block text-[13.5px] font-medium text-[#E5DEFA] mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}

function DetailRow({ icon: Icon, label, value, href, testId, external }) {
  const content = (
    <div className="flex items-start gap-4">
      <div className="grid place-items-center w-11 h-11 rounded-2xl bg-[#2D1456] border border-[#4A2D7A] text-[#5FE6E0] shrink-0">
        <Icon className="w-5 h-5" strokeWidth={1.6} />
      </div>
      <div>
        <div className="text-[12px] uppercase tracking-[0.22em] text-[#8B7FB5] font-medium">
          {label}
        </div>
        <div className="text-[16px] mt-1 text-white" style={{ fontFamily: "Outfit" }}>
          {value}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      data-testid={testId}
      target={external ? "_blank" : undefined}
      rel={external ? "noopener noreferrer" : undefined}
      className="block group hover:translate-x-0.5 transition-transform"
    >
      {content}
    </a>
  ) : (
    <div data-testid={testId}>{content}</div>
  );
}

function SuccessMessage({ onAnother }) {
  return (
    <div data-testid="form-success" className="text-center py-8">
      <div className="mx-auto w-16 h-16 rounded-full bg-[#2BB8B4] grid place-items-center text-[#0E0524] lfc-glow-teal">
        <CalendarCheck2 className="w-7 h-7" strokeWidth={1.8} />
      </div>
      <h3 className="mt-6 text-2xl font-semibold text-white" style={{ fontFamily: "Outfit" }}>
        Thank you — your enquiry has been received.
      </h3>
      <p className="mt-3 text-[15.5px] text-[#C2B4E6] max-w-md mx-auto leading-relaxed">
        We'll be in touch within one working day to confirm your appointment.
        For urgent matters, please call us directly.
      </p>
      <div className="mt-7 flex justify-center gap-3 flex-wrap">
        <a href={`tel:${CLINIC.phoneRaw}`} className="lfc-btn-secondary">
          <Phone className="w-4 h-4" strokeWidth={1.6} />
          {CLINIC.phone}
        </a>
        <button onClick={onAnother} className="lfc-btn-primary" data-testid="form-another">
          Submit another enquiry
        </button>
      </div>
    </div>
  );
}
